import { useState } from "react";
import { Sparkles, Loader2, TrendingUp, AlertTriangle, Share2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";

export const AIResearchPanel = ({ ticker }) => {
  const [loading, setLoading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/stocks/${ticker}/ai-summary`);
      setResult(data);
    } catch (e) {
      toast.error(apiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };

  const share = async () => {
    setSharing(true);
    try {
      const { data } = await api.post("/share/cards", { ticker });
      const url = `${window.location.origin}/share/${data.id}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Public research card created — link copied", { description: url });
      } catch {
        toast.success("Public research card created", { description: url });
      }
    } catch (e) {
      toast.error(apiErrorDetail(e));
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="widget p-6 bg-blue-950/20 border-primary/20" data-testid="ai-research-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <h3 className="font-heading font-bold text-lg text-white tracking-tight">AI Research Summary</h3>
        </div>
        {result && (
          <div className="flex items-center gap-3">
            <button onClick={share} disabled={sharing} data-testid="share-research-card-button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border border-primary/40 text-primary rounded-sm hover:bg-primary/10 transition-colors duration-150">
              {sharing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />}
              Share research card
            </button>
            <span className="text-[11px] font-mono text-muted-foreground" data-testid="ai-summary-meta">
              {result.cached ? "CACHED · " : "FRESH · "}
              {new Date(result.generated_at).toLocaleString()}
            </span>
          </div>
        )}
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        Claude Sonnet 4.5 analyses cached fundamentals, technicals and headlines for {ticker}.
      </p>

      {!result && (
        <Button
          onClick={generate}
          disabled={loading}
          data-testid="generate-ai-summary-button"
          className="rounded-sm bg-primary hover:bg-primary/85 text-white"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analysing {ticker}…
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" /> Generate AI Research
            </>
          )}
        </Button>
      )}

      {result && (
        <div className="space-y-6 fade-up" data-testid="ai-summary-content">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-gain" />
                <span className="overline-label text-gain">Attractive factors</span>
              </div>
              <ul className="space-y-2" data-testid="ai-attractive-factors">
                {result.summary.attractive_factors.map((f, i) => (
                  <li key={i} className="text-sm text-gray-100 border-l-2 border-gain/50 pl-3 leading-relaxed">
                    {f}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-loss" />
                <span className="overline-label text-loss">Risk factors</span>
              </div>
              <ul className="space-y-2" data-testid="ai-risk-factors">
                {result.summary.risk_factors.map((f, i) => (
                  <li key={i} className="text-sm text-gray-100 border-l-2 border-loss/50 pl-3 leading-relaxed">
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <span className="overline-label">Valuation view</span>
              <p className="text-sm text-gray-300 mt-2 leading-relaxed" data-testid="ai-valuation-view">
                {result.summary.valuation_view}
              </p>
            </div>
            <div>
              <span className="overline-label">Technical view</span>
              <p className="text-sm text-gray-300 mt-2 leading-relaxed" data-testid="ai-technical-view">
                {result.summary.technical_view}
              </p>
            </div>
          </div>

          <p className="text-sm text-gray-400 italic border border-border rounded-sm p-3" data-testid="ai-verdict-note">
            {result.summary.verdict_style_note}
          </p>
        </div>
      )}

      <p className="text-xs text-muted-foreground mt-6 pt-4 border-t border-white/10" data-testid="ai-disclaimer">
        This is AI-generated research, not financial advice.
      </p>
    </div>
  );
};
